// src/utils/avatar.js - Updated with custom avatars (fixed version)
import { createAvatar } from '@dicebear/core';
import {
  notionists,
  avataaars,
  bigSmile,
  funEmoji,
  personas,
  miniavs
} from '@dicebear/collection';

// Map style IDs to actual style imports
const styleMap = {
  'notionists': notionists,
  'avataaars': avataaars,
  'big-smile': bigSmile,
  'fun-emoji': funEmoji,
  'personas': personas,
  'miniavs': miniavs
};

// Your optimized custom avatar images (now in public folder)
export const getCustomAvatars = () => {
  return [
    {
      id: 'avatar-1',
      name: 'Colorful Girl',
      url: '/images/avatars/afro-mix.webp ', // Update extension to match your files
      description: 'Modern and vibrant'
    },
    {
      id: 'avatar-2',
      name: 'Professional Guy',
      url: '/images/avatars/afro-mustache.webp ',
      description: 'Clean and professional'
    },
    {
      id: 'avatar-3',
      name: 'Casual Style',
      url: '/images/avatars/afro-pink.webp ',
      description: 'Relaxed and friendly'
    },
    {
      id: 'avatar-4',
      name: 'Creative Type',
      url: '/images/avatars/afro-purple.webp ',
      description: 'Artistic and unique'
    },
    {
      id: 'avatar-5',
      name: 'Business Pro',
      url: '/images/avatars/blonde-white.webp ',
      description: 'Corporate ready'
    },
    {
      id: 'avatar-6',
      name: 'Cool Character',
      url: '/images/avatars/blue-no-beard.webp ',
      description: 'Trendy and stylish'
    },
    {
      id: 'avatar-7',
      name: 'Smart Scholar',
      url: '/images/avatars/brown-beard.webp ',
      description: 'Academic and intelligent'
    },
    {
      id: 'avatar-8',
      name: 'Tech Guru',
      url: '/images/avatars/dark-beard.webp ',
      description: 'Modern tech enthusiast'
    },
    {
      id: 'avatar-9',
      name: 'Friendly Face',
      url: '/images/avatars/dark-hat-woman.webp ',
      description: 'Warm and approachable'
    },
    {
      id: 'avatar-10',
      name: 'Minimalist',
      url: '/images/avatars/dark-hat.webp ',
      description: 'Clean and simple'
    },
    {
      id: 'avatar-11',
      name: 'Adventurer',
      url: '/images/avatars/dark-no-glass.webp ',
      description: 'Outdoorsy and active'
    },
    {
      id: 'avatar-12',
      name: 'Artist',
      url: '/images/avatars/no-beard.webp ',
      description: 'Creative and expressive'
    },
    {
      id: 'avatar-13',
      name: 'Professional',
      url: '/images/avatars/orange-white.webp ',
      description: 'Formal and polished'
    },
    {
      id: 'avatar-14',
      name: 'Student',
      url: '/images/avatars/white-hat.webp ',
      description: 'Young and studious'
    },
    {
      id: 'avatar-15',
      name: 'Trendsetter',
      url: '/images/avatars/white-hat-woman.webp ',
      description: 'Fashion-forward'
    },
    {
      id: 'avatar-16',
      name: 'Nature Lover',
      url: '/images/avatars/white-mix.webp ',
      description: 'Earth-friendly vibes'
    },
    {
      id: 'avatar-17',
      name: 'Classic Style',
      url: '/images/avatars/white-mustache.webp ',
      description: 'Timeless and elegant'
    }
  ];
};

// Check if a URL is a custom avatar
export const isCustomAvatar = (url) => {
  return url && url.startsWith('/images/avatars/');
};

// Get avatar by ID
export const getAvatarById = (id) => {
  const avatars = getCustomAvatars();
  return avatars.find(avatar => avatar.id === id);
};

// Get a random custom avatar
export const getRandomCustomAvatar = () => {
  const avatars = getCustomAvatars();
  const randomIndex = Math.floor(Math.random() * avatars.length);
  return avatars[randomIndex];
};

// Enhanced default avatar generation with custom avatar fallback
export const generateDefaultAvatar = (seed) => {
  if (!seed) {
    const randomAvatar = getRandomCustomAvatar();
    return randomAvatar ? randomAvatar.url : `https://placehold.co/128x128/3b82f6/ffffff?text=U`;
  }

  try {
    const svg = createAvatar(notionists, {
      seed: seed,
      size: 128,
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffdfbf', 'f0d4a8'],
      backgroundType: 'gradient',
    }).toString();

    const dataUri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
    return dataUri;
  } catch (error) {
    console.warn('Error generating avatar, using custom avatar fallback:', error);
    const randomAvatar = getRandomCustomAvatar();
    if (randomAvatar) {
      return randomAvatar.url;
    }

    const initials = seed.substring(0, 2).toUpperCase();
    return `https://placehold.co/128x128/3b82f6/ffffff?text=${initials}`;
  }
};

export const generateAvatarWithStyle = (seed, style = 'notionists') => {
  if (!seed) {
    return `https://placehold.co/128x128/3b82f6/ffffff?text=U`;
  }

  try {
    const avatarStyle = styleMap[style] || notionists;

    // Define different color schemes for variety
    const colorSchemes = {
      'notionists': {
        backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffdfbf', 'f0d4a8'],
        backgroundType: 'gradient'
      },
      'avataaars': {
        backgroundColor: ['ffdfbf', 'f0d4a8', 'ffd3a5'],
        backgroundType: 'solid'
      },
      'big-smile': {
        backgroundColor: ['a8e6cf', 'dcedc8', 'c8f7c5'],
        backgroundType: 'gradient'
      },
      'fun-emoji': {
        backgroundColor: ['ff9a9e', 'fecfef', 'ffc3a0'],
        backgroundType: 'solid'
      },
      'personas': {
        backgroundColor: ['667eea', '764ba2', '6a82fb'],
        backgroundType: 'gradient'
      },
      'miniavs': {
        backgroundColor: ['48cae4', '90e0ef', '00b4d8'],
        backgroundType: 'solid'
      }
    };

    const colors = colorSchemes[style] || colorSchemes['notionists'];

    const svg = createAvatar(avatarStyle, {
      seed: seed,
      size: 128,
      ...colors
    }).toString();

    const dataUri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
    return dataUri;
  } catch (error) {
    console.warn(`Error generating ${style} avatar:`, error);
    return generateDefaultAvatar(seed);
  }
};

export const getAvatarStyles = () => {
  return [
    { id: 'notionists', name: 'Modern', description: 'Clean and professional' },
    { id: 'avataaars', name: 'Cartoon', description: 'Fun and colorful' },
    { id: 'big-smile', name: 'Happy', description: 'Always smiling' },
    { id: 'fun-emoji', name: 'Emoji', description: 'Emoji-style faces' },
    { id: 'personas', name: 'Professional', description: 'Business-ready' },
    { id: 'miniavs', name: 'Minimal', description: 'Simple and clean' }
  ];
};

// Generate multiple avatar variations for selection (mix of generated and custom)
export const generateAvatarVariations = (baseSeed, count = 6, style = 'notionists') => {
  const variations = [];
  const customAvatars = getCustomAvatars();

  // Mix generated and custom avatars
  const useCustomCount = Math.min(count, customAvatars.length);
  const useGeneratedCount = count - useCustomCount;

  // Add custom avatars
  for (let i = 0; i < useCustomCount; i++) {
    if (customAvatars[i]) {
      variations.push({
        id: customAvatars[i].id,
        url: customAvatars[i].url,
        name: customAvatars[i].name,
        type: 'custom'
      });
    }
  }

  // Add generated avatars if needed
  for (let i = 0; i < useGeneratedCount; i++) {
    const seed = `${baseSeed}-var${i}`;
    const avatar = generateAvatarWithStyle(seed, style);
    variations.push({
      id: `${style}-${i}`,
      url: avatar,
      seed: seed,
      style: style,
      type: 'generated'
    });
  }

  return variations;
};

// Get avatar URL with proper fallback system
export const getAvatarUrl = (user) => {
  if (user?.photoURL) {
    return user.photoURL;
  }

  // Try to get a custom avatar first
  const randomAvatar = getRandomCustomAvatar();
  if (randomAvatar) {
    return randomAvatar.url;
  }

  // Final fallback to placeholder with initials
  const initials = user?.displayName?.split(' ').map(n => n[0]).join('') || 'U';
  return `https://placehold.co/128x128/3b82f6/ffffff?text=${initials}`;
};